import React, { useState, useEffect } from 'react';
import { FiPhone, FiMessageCircle, FiChevronLeft, FiChevronRight, FiPlay, FiPause, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isNewsPaused, setIsNewsPaused] = useState(false);

  const slides = [
    {
      id: 1,
      headline: "Join Our Global Community of Young Leaders",
      tagline: "Premium early childhood education that nurtures creativity, confidence, and academic excellence in a world-class environment.",
      cta: "Enroll Now",
      image: "/img/hero/hero1.jpg",
      bgGradient: "from-blue-50 via-indigo-50 to-purple-50"
    },
    {
      id: 2,
      headline: "STEM Excellence from Age 3",
      tagline: "Cutting-edge robotics, coding, and science programs designed for tomorrow's innovators and problem-solvers.",
      cta: "Learn More",
      image: "/img/hero/hero2.jpg",
      bgGradient: "from-emerald-50 via-teal-50 to-cyan-50"
    },
    {
      id: 3,
      headline: "Bilingual Education for Global Citizens",
      tagline: "Dual language immersion programs that prepare children for an interconnected world with cultural fluency.",
      cta: "Learn More",
      image: "/img/hero/hero3.jpg",
      bgGradient: "from-amber-50 via-orange-50 to-rose-50"
    }
  ];

  const newsItems = [
    "🎓 Admissions Open for 2024-25 Academic Year",
    "🏆 Aksharam Students Excel in International Math Olympiad",
    "🌟 New STEM Lab Opening This Month",
    "📚 Bilingual Program Now Available for Ages 2-6",
    "🎨 Art Exhibition: Student Creations on Display",
    "⚽ Sports Day Registration Now Open"
  ];

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section className="relative min-h-screen md:h-screen flex flex-col overflow-hidden">
      {/* Hero Carousel */}
      <div className="flex-1 relative">
        {/* Background with dynamic gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${slides[currentSlide].bgGradient}`}>
          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"></div>
        </div>

        {/* Abstract Shapes */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-blue-200/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-emerald-200/20 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-purple-200/20 rounded-full blur-lg animate-bounce"></div>

        {/* Carousel Container */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 h-full flex items-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center w-full">
            {/* Content Side */}
            <div className="space-y-4 lg:space-y-8">
              {/* Trust Badge */}
              <div className="flex items-center space-x-2 text-emerald-600" data-aos="fade-up">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-500 fill-current" />
                  ))}
                </div>
                <span className="text-sm font-medium">ISO 9001:2015 Certified</span>
              </div>

              {/* Dynamic Headline */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight transition-all duration-500" data-aos="fade-up" data-aos-delay="100">
                {slides[currentSlide].headline.split(' ').map((word, index) => (
                  <span key={index} className={index >= slides[currentSlide].headline.split(' ').length - 2 ? 'text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600' : ''}>
                    {word}{' '}
                  </span>
                ))}
              </h1>

              {/* Tagline */}
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl transition-all duration-500" data-aos="fade-up" data-aos-delay="200">
                {slides[currentSlide].tagline}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-row gap-3" data-aos="fade-up" data-aos-delay="300">
                <button className="flex items-center justify-center space-x-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3 rounded-xl text-base font-semibold hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl">
                  <span>{slides[currentSlide].cta}</span>
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                </button>
                <a
                  href="tel:+917416706111"
                  className="flex items-center justify-center space-x-2 bg-white/90 backdrop-blur-sm border-2 border-gray-200 text-gray-700 px-4 py-3 rounded-xl text-base font-semibold hover:bg-white hover:border-emerald-300 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span>Schedule Visit</span>
                </a>
              </div>
            </div>

            {/* Image Side */}
            <div className="relative order-first lg:order-last" data-aos="fade-left" data-aos-delay="300">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src={slides[currentSlide].image}
                  alt="THE ASIAN INTERNATIONAL SCHOOL"
                  className="w-full aspect-square lg:aspect-[4/3] object-cover transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Carousel Controls */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:flex items-center space-x-4">
            {/* Previous Button */}
            <button
              onClick={prevSlide}
              className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-700 hover:bg-white hover:text-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl"
              aria-label="Previous slide"
            >
              <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
            </button>

            {/* Slide Indicators */}
            <div className="flex space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide ? 'bg-emerald-600 scale-125' : 'bg-white/60 hover:bg-white/80'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={nextSlide}
              className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-700 hover:bg-white hover:text-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl"
              aria-label="Next slide"
            >
              <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
            </button>

            {/* Play/Pause Button */}
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-700 hover:bg-white hover:text-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl ml-4"
              aria-label={isAutoPlaying ? 'Pause autoplay' : 'Start autoplay'}
            >
              <SafeIcon icon={isAutoPlaying ? FiPause : FiPlay} className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* News Ticker */}
      <div className="bg-emerald-900 text-white py-3 relative overflow-hidden">
        <div className="flex items-center space-x-8 whitespace-nowrap">
          <div
            className={`flex space-x-8 animate-scroll ${isNewsPaused ? 'animation-paused' : ''}`}
            onMouseEnter={() => setIsNewsPaused(true)}
            onMouseLeave={() => setIsNewsPaused(false)}
            style={{
              animation: 'scroll 30s linear infinite'
            }}
          >
            {/* Duplicate news items for seamless loop */}
            {[...newsItems, ...newsItems].map((item, index) => (
              <span key={index} className="text-sm font-medium flex items-center space-x-2">
                <span>{item}</span>
                <span className="text-emerald-300">•</span>
              </span>
            ))}
          </div>
        </div>

        {/* Gradient overlays for smooth edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-emerald-900 to-transparent"></div>
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-emerald-900 to-transparent"></div>
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-scroll {
          animation: scroll 30s linear infinite;
        }

        .animation-paused {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default Hero;